import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Plus, Search, Download, Upload, Trash2, Printer,
  CheckSquare, Square, Image, AlertTriangle, X,
  CheckCircle, XCircle, Camera, Filter, Users,
  GraduationCap, Eye, Edit, ChevronLeft, ChevronRight, Moon, BookOpen,
  BarChart3, RefreshCw,
} from 'lucide-react';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { useNavigate } from 'react-router-dom';
import ScrollToTop from '../../components/ui/ScrollToTop';
import { fetchEtudiants } from '../../store/slices/etudiantSlice';
import { useGetAnneesAcademiquesQuery } from '../../services/anneeAcademiqueApi';
import {
  useGetChoixFormulaireQuery,
  useSupprimerMultipleMutation,
  useImporterPhotosMutation,
  useCheckDoublonsQuery,
  useFusionnerEtudiantMutation,
  useDetecterClassesDoublonsQuery,
  useFusionnerClassesMutation,
} from '../../services/etudiantsApi';
// Fix 2026-09-10 : pour filtrer classes/options par niveau+pôle
import { useGetClassesQuery, useGetOptionsQuery } from '../../services/polesOptionsApi';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Spinner } from '../../components/ui/Spinner';
import { PageHeader } from '../../components/ui/PageHeader';
import { AlertBanner } from '../../components/ui/AlertBanner';

import { Merge, ArrowRight as ArrowRightIcon } from 'lucide-react';

type ModalType = 'none' | 'deleteSelected' | 'importPhotos' | 'printConfig';

interface PrintColumn {
  key: string;
  label: string;
  enabled: boolean;
  align?: 'left' | 'center';
}

const DEFAULT_PRINT_COLUMNS: PrintColumn[] = [
  { key: 'numero', label: 'N°', enabled: true, align: 'center' },
  { key: 'matricule', label: 'Matricule', enabled: true },
  { key: 'nom', label: 'Nom', enabled: true },
  { key: 'prenoms', label: 'Prénoms', enabled: true },
  { key: 'sexe', label: 'Sexe', enabled: false, align: 'center' },
  { key: 'niveau', label: 'Niveau', enabled: true, align: 'center' },
  { key: 'classe', label: 'Classe', enabled: true, align: 'center' },
  { key: 'option', label: 'Option', enabled: true },
  { key: 'pole', label: 'Pôle', enabled: true },
  { key: 'telephone', label: 'Téléphone', enabled: false },
  { key: 'email', label: 'Email', enabled: false },
  { key: 'date_naissance', label: 'Date de naissance', enabled: false },
  { key: 'lieu_naissance', label: 'Lieu de naissance', enabled: false },
  { key: 'statut', label: 'Statut', enabled: false, align: 'center' },
];

export const EtudiantsList: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { etudiants, loading, totalCount } = useAppSelector((state) => state.etudiants);
  const { data: choixFormulaire } = useGetChoixFormulaireQuery();
  // Fix 2026-09-10 : mapping code_classe → {niveau, pole_nom, option_nom}
  const { data: allClasses = [] } = useGetClassesQuery({ actif: true });
  const { data: allOptions = [] } = useGetOptionsQuery({ actif: true });
  const { data: annees = [] } = useGetAnneesAcademiquesQuery({});
  const anneeActive = annees.find(a => a.est_active);
  const anneeAcademique = anneeActive?.libelle || '2025-2026';

  const [supprimerMultiple, { isLoading: deletingMultiple }] = useSupprimerMultipleMutation();
  const [importerPhotos, { isLoading: importingPhotos }] = useImporterPhotosMutation();
  const [fusionnerEtudiant, { isLoading: fusionning }] = useFusionnerEtudiantMutation();
  const { data: doublonsData, refetch: refetchDoublons } = useCheckDoublonsQuery();

  const [showDoublons, setShowDoublons] = useState(false);
  const [showFusionClasses, setShowFusionClasses] = useState(false);
  const { data: classesDoublons, refetch: refetchClassesDoublons } = useDetecterClassesDoublonsQuery(undefined, { skip: !showFusionClasses });
  const [fusionnerClasses, { isLoading: fusionnantClasses }] = useFusionnerClassesMutation();
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filters, setFilters] = useState({
    niveau: '',
    option: '',
    pole: '',
    classe: '',
    actif: 'true',
    type_formation: '',
  });
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [activeModal, setActiveModal] = useState<ModalType>('none');
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);
  const [photoFiles, setPhotoFiles] = useState<File[]>([]);
  const [photoResult, setPhotoResult] = useState<any>(null);
  // Filtres visibles par défaut (le user tape rarement sans filtrer).
  // Persisté en localStorage pour respecter le choix d'affichage.
  const [showFilters, setShowFilters] = useState<boolean>(
    () => localStorage.getItem('etudiants_show_filters') !== '0'
  );
  useEffect(() => {
    localStorage.setItem('etudiants_show_filters', showFilters ? '1' : '0');
  }, [showFilters]);
  // Bloc statistiques (cards niveaux/pôles + tableau détaillé) : masqué par
  // défaut pour laisser la liste des étudiants immédiatement visible sur
  // atterrissage. Un chip inline compact reste toujours visible en haut,
  // le bouton « + Stats » développe les blocs détaillés.
  const [showStats, setShowStats] = useState<boolean>(
    () => localStorage.getItem('etudiants_show_stats') === '1'
  );
  useEffect(() => {
    localStorage.setItem('etudiants_show_stats', showStats ? '1' : '0');
  }, [showStats]);
  const [printColumns, setPrintColumns] = useState<PrintColumn[]>(DEFAULT_PRINT_COLUMNS.map(c => ({ ...c })));
  const [printSignatureCol, setPrintSignatureCol] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  // pageSize = 0 signifie "tous les étudiants" (pas de pagination côté back)
  const [pageSize, setPageSize] = useState<number>(50);

  const photoInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => setMessage(null), 6000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  // Debounce de la recherche : attend 300ms après la dernière frappe
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const loadEtudiants = useCallback(() => {
    // pageSize=0 → « Tous » : on n'envoie pas `page` pour que OptionalPagination
    // renvoie l'intégralité de la queryset filtrée.
    const params: Record<string, any> = pageSize > 0
      ? { page: currentPage, page_size: pageSize }
      : {};
    if (debouncedSearch) params.search = debouncedSearch;
    if (filters.niveau) params.niveau = filters.niveau;
    if (filters.option) params.option = filters.option;
    if (filters.pole) params.pole = filters.pole;
    if (filters.classe) params.classe = filters.classe;
    if (filters.actif) params.actif = filters.actif;
    if (filters.type_formation) params.type_formation = filters.type_formation;
    dispatch(fetchEtudiants(params));
  }, [dispatch, debouncedSearch, filters, currentPage]);

  useEffect(() => {
    loadEtudiants();
  }, [loadEtudiants]);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, filters]);

  // Handlers
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    loadEtudiants();
  };

  const handleSelectAll = () => {
    if (selectedIds.size === etudiants.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(etudiants.map(e => e.id)));
    }
  };

  const handleSelectOne = (id: number) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleDeleteSelected = async () => {
    if (selectedIds.size === 0) return;
    try {
      const result = await supprimerMultiple(Array.from(selectedIds)).unwrap();
      setMessage({ type: 'success', text: result.message });
      setSelectedIds(new Set());
      setActiveModal('none');
      loadEtudiants();
    } catch {
      setMessage({ type: 'error', text: 'Erreur lors de la suppression' });
    }
  };

  const handleExport = () => {
    const params = new URLSearchParams();
    if (filters.niveau) params.append('niveau', filters.niveau);
    if (filters.classe) params.append('classe', filters.classe);
    if (filters.pole) params.append('pole', filters.pole);
    if (filters.option) params.append('option', filters.option);
    if (filters.actif) params.append('actif', filters.actif);

    const token = localStorage.getItem('token');
    const url = `${window.location.hostname === 'localhost' ? 'http://localhost:8000' : ''}/api/etudiants/exporter_excel/?${params.toString()}`;

    fetch(url, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(res => res.blob())
      .then(blob => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `etudiants_${filters.niveau || 'tous'}.xlsx`;
        a.click();
        URL.revokeObjectURL(a.href);
        setMessage({ type: 'success', text: 'Export Excel téléchargé' });
      })
      .catch(() => setMessage({ type: 'error', text: "Erreur lors de l'export" }));
  };

  const getStudentCellValue = (e: any, key: string, index: number): string => {
    switch (key) {
      case 'numero': return `${(currentPage - 1) * pageSize + index + 1}`;
      case 'matricule': return e.matricule || '';
      case 'nom': return e.nom || '';
      case 'prenoms': return e.prenoms || e.prenom || '';
      case 'sexe': return e.sexe || '';
      case 'niveau': return e.niveau || '';
      case 'classe': return e.classe || '';
      case 'option': return e.option || '';
      case 'pole': return e.pole || '';
      case 'telephone': return e.telephone || '';
      case 'email': return e.email || '';
      case 'date_naissance': return e.date_naissance ? new Date(e.date_naissance).toLocaleDateString('fr-FR') : '';
      case 'lieu_naissance': return e.lieu_naissance || '';
      case 'statut': return e.actif ? 'Actif' : 'Inactif';
      default: return '';
    }
  };

  const handlePrint = () => {
    const activeCols = printColumns.filter(c => c.enabled);
    if (activeCols.length === 0) return;

    const filterDesc: string[] = [];
    if (filters.niveau) filterDesc.push(`Niveau: ${filters.niveau}`);
    if (filters.classe) filterDesc.push(`Classe: ${filters.classe}`);
    if (filters.pole) filterDesc.push(`Pôle: ${filters.pole}`);
    if (filters.option) filterDesc.push(`Option: ${filters.option}`);

    const headerCells = activeCols.map(col =>
      `<th style="${col.align === 'center' ? 'text-align:center;' : 'text-align:left;'}${col.key === 'numero' ? 'width:35px;' : ''}">${col.label}</th>`
    ).join('');

    const signatureHeader = printSignatureCol ? '<th style="width:80px;text-align:center">Signature</th>' : '';

    const rows = etudiants.map((e, i) => {
      const cells = activeCols.map(col =>
        `<td style="${col.align === 'center' ? 'text-align:center;' : ''}padding:6px;border:1px solid #ddd">${getStudentCellValue(e, col.key, i)}</td>`
      ).join('');
      const signatureCell = printSignatureCol ? '<td style="padding:6px;border:1px solid #ddd">&nbsp;</td>' : '';
      return `<tr>${cells}${signatureCell}</tr>`;
    }).join('');

    const html = `<!DOCTYPE html><html><head><meta charset="utf-8">
<title>Liste des Étudiants - Institut 2i</title>
<style>
  @page { margin: 1.5cm; }
  body { font-family: 'Segoe UI', Arial, sans-serif; margin: 0; padding: 20px; }
  .header { text-align: center; margin-bottom: 24px; border-bottom: 3px solid #1F4E79; padding-bottom: 16px; }
  .header img { height: 80px; margin-bottom: 8px; }
  .header h1 { font-size: 20px; color: #1F4E79; margin: 4px 0; letter-spacing: 1px; }
  .header h2 { font-size: 14px; color: #555; margin: 4px 0; font-weight: normal; }
  .meta { display: flex; justify-content: space-between; font-size: 11px; color: #666; margin-bottom: 12px; }
  .filters { text-align: center; font-size: 12px; color: #1F4E79; margin-bottom: 12px; font-weight: 600; }
  table { width: 100%; border-collapse: collapse; font-size: 10.5px; }
  th { background: #1F4E79; color: white; padding: 8px 6px; text-align: left; }
  td { padding: 6px; border: 1px solid #ddd; }
  tr:nth-child(even) td { background: #f5f7fa; }
  .footer { text-align: center; margin-top: 20px; font-size: 10px; color: #999; border-top: 1px solid #ddd; padding-top: 8px; }
</style></head><body>
<div class="header">
  <img src="/logo.png" alt="Logo 2i" onerror="this.style.display='none'"/>
  <h1>INSTITUT INTERNATIONAL 2i</h1>
  <h2>Liste des Étudiants</h2>
</div>
<div class="meta">
  <span>Année académique: 2025-2026</span>
  <span>Date: ${new Date().toLocaleDateString('fr-FR')}</span>
</div>
${filterDesc.length ? `<div class="filters">${filterDesc.join(' &bull; ')}</div>` : ''}
<table>
  <thead><tr>${headerCells}${signatureHeader}</tr></thead>
  <tbody>${rows}</tbody>
</table>
<div class="footer">Total: ${etudiants.length} étudiant(s) &mdash; Institut International 2i </div>
</body></html>`;

    const w = window.open('', '_blank');
    if (w) {
      w.document.write(html);
      w.document.close();
      w.focus();
      setTimeout(() => w.print(), 500);
    }
    setActiveModal('none');
  };

  const handlePhotoFilesSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setPhotoFiles(Array.from(e.target.files));
      setPhotoResult(null);
    }
  };

  const handleImportPhotos = async () => {
    if (photoFiles.length === 0) return;
    try {
      const result = await importerPhotos({
        photos: photoFiles,
        annee_academique: '2025-2026',
      }).unwrap();
      setPhotoResult(result);
      setMessage({
        type: result.non_associees > 0 ? 'info' : 'success',
        text: `${result.associees}/${result.total} photo(s) associée(s)`
      });
      if (result.associees > 0) loadEtudiants();
    } catch {
      setMessage({ type: 'error', text: "Erreur lors de l'import des photos" });
    }
  };

  const resetFilters = () => {
    setFilters({ niveau: '', option: '', pole: '', classe: '', actif: 'true', type_formation: '' });
    setSearchQuery('');
  };

  // ── Impression des effectifs détaillés ──
  const handlePrintEffectifs = useCallback(() => {
    if (!choixFormulaire) return;

    const _statsNiveaux = choixFormulaire.niveaux || [];
    const _statsPoles = choixFormulaire.poles || [];
    const _polesNiveaux = choixFormulaire.poles_niveaux || [];
    const _classes = choixFormulaire.classes || [];
    const _options = choixFormulaire.options || [];
    const _csDdPN = choixFormulaire.cs_dd_par_pole_niveau ?? {};
    const _csDdNiv = choixFormulaire.cs_dd_par_niveau ?? {};
    const _csTotal = choixFormulaire.cs_total ?? 0;
    const _ddTotal = choixFormulaire.dd_total ?? 0;
    const _totalEtudiants = _statsNiveaux.reduce((s, n) => s + n.count, 0);
    const _repSexe = (choixFormulaire as any).repartition_sexe_detail ?? { FI: { poles: {}, total: 0, total_H: 0, total_F: 0 }, FC: { poles: {}, total: 0, total_H: 0, total_F: 0 } };

    const nivCols = ['L1', 'L2', 'L3', 'M1', 'M2'].filter(niv =>
      _polesNiveaux.some(p => (p.niveaux[niv] ?? 0) > 0)
    );
    const csDdNivCols = ['L1', 'L2', 'L3', 'M1', 'M2'].filter(niv => _csDdNiv[niv]);
    const csDdPoleKeys = Object.keys(_csDdPN);

    const today = new Date().toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });

    // ── Helpers SVG ──
    const POLE_COLORS = ['#1F4E79', '#E67E22', '#27AE60', '#8E44AD', '#E74C3C'];
    const NIV_COLORS  = ['#3498DB', '#2ECC71', '#F39C12', '#E74C3C', '#9B59B6'];

    // Génère un camembert SVG
    const buildPieChart = (
      data: { label: string; value: number; color: string }[],
      size: number,
      title: string,
    ): string => {
      const total = data.reduce((s, d) => s + d.value, 0);
      if (total === 0) return '';
      const cx = size / 2;
      const cy = size / 2;
      const r = size / 2 - 4;
      let angle = -90;
      const slices = data.filter(d => d.value > 0).map(d => {
        const pct = d.value / total;
        const sweep = pct * 360;
        const startAngle = angle;
        angle += sweep;
        const endAngle = angle;
        const startRad = (startAngle * Math.PI) / 180;
        const endRad = (endAngle * Math.PI) / 180;
        const x1 = cx + r * Math.cos(startRad);
        const y1 = cy + r * Math.sin(startRad);
        const x2 = cx + r * Math.cos(endRad);
        const y2 = cy + r * Math.sin(endRad);
        const largeArc = sweep > 180 ? 1 : 0;
        const path = `M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2} Z`;
        return { ...d, path, pct };
      });

      const legendY = size + 12;
      const legendItems = slices.map((s, i) => {
        const ly = legendY + i * 14;
        return `<rect x="0" y="${ly}" width="10" height="10" fill="${s.color}" rx="1"/>
          <text x="14" y="${ly + 9}" font-size="9" fill="#333">${s.label}: ${s.value} (${(s.pct * 100).toFixed(1)}%)</text>`;
      }).join('');

      const svgH = legendY + slices.length * 14 + 4;
      return `<div style="display:inline-block;text-align:center;vertical-align:top;">
        <div style="font-weight:bold;font-size:11px;margin-bottom:4px;color:#1F4E79;">${title}</div>
        <svg width="${size}" height="${svgH}" xmlns="http://www.w3.org/2000/svg">
          ${slices.map(s => `<path d="${s.path}" fill="${s.color}" stroke="#fff" stroke-width="1"/>`).join('')}
          ${legendItems}
        </svg>
      </div>`;
    };

    // Génère un graphique barres horizontales SVG
    const buildBarChart = (
      data: { label: string; value: number; color: string }[],
      width: number,
      title: string,
    ): string => {
      const maxVal = Math.max(...data.map(d => d.value), 1);
      const barH = 18;
      const gap = 6;
      const labelW = 35;
      const valueW = 40;
      const barArea = width - labelW - valueW - 10;
      const svgH = data.length * (barH + gap) + 4;

      const bars = data.map((d, i) => {
        const y = i * (barH + gap) + 2;
        const bw = (d.value / maxVal) * barArea;
        return `<text x="0" y="${y + 13}" font-size="10" fill="#333" font-weight="600">${d.label}</text>
          <rect x="${labelW}" y="${y}" width="${bw}" height="${barH}" fill="${d.color}" rx="2"/>
          <text x="${labelW + bw + 4}" y="${y + 13}" font-size="10" fill="#333" font-weight="bold">${d.value}</text>`;
      }).join('');

      return `<div style="display:inline-block;vertical-align:top;">
        <div style="font-weight:bold;font-size:11px;margin-bottom:4px;color:#1F4E79;">${title}</div>
        <svg width="${width}" height="${svgH}" xmlns="http://www.w3.org/2000/svg">${bars}</svg>
      </div>`;
    };

    // Données pour les graphiques
    const piePolesData = _statsPoles.filter(p => p.count > 0).map((p, i) => ({
      label: p.label, value: p.count, color: POLE_COLORS[i % POLE_COLORS.length],
    }));
    const barNiveauxData = _statsNiveaux.filter(n => n.count > 0).map((n, i) => ({
      label: n.code, value: n.count, color: NIV_COLORS[i % NIV_COLORS.length],
    }));
    const fiTotal = _totalEtudiants - _csTotal - _ddTotal;
    const pieFormationData = [
      { label: 'Formation Initiale (FI)', value: fiTotal, color: '#3498DB' },
      ..._csTotal > 0 ? [{ label: 'Cours du Soir (CS)', value: _csTotal, color: '#E67E22' }] : [],
      ..._ddTotal > 0 ? [{ label: 'Double Diplôme (DD)', value: _ddTotal, color: '#8E44AD' }] : [],
    ];

    // Graphique barres groupées pôle × niveau
    const buildGroupedBarChart = (): string => {
      const poles = _polesNiveaux.filter(p => Object.values(p.niveaux).some(v => v > 0));
      if (poles.length === 0 || nivCols.length === 0) return '';
      const maxVal = Math.max(...poles.flatMap(p => nivCols.map(n => p.niveaux[n] ?? 0)), 1);
      const groupW = 50;
      const barW = Math.min(12, Math.floor((groupW - 4) / nivCols.length));
      const chartH = 120;
      const labelH = 40;
      const svgW = poles.length * (groupW + 16) + 20;
      const svgH = chartH + labelH + 30;

      const groups = poles.map((p, gi) => {
        const gx = 10 + gi * (groupW + 16);
        const bars = nivCols.map((niv, ni) => {
          const val = p.niveaux[niv] ?? 0;
          const bh = (val / maxVal) * (chartH - 10);
          const bx = gx + ni * (barW + 1);
          const by = chartH - bh;
          const col = NIV_COLORS[ni % NIV_COLORS.length];
          return `<rect x="${bx}" y="${by}" width="${barW}" height="${bh}" fill="${col}" rx="1"/>
            ${val > 0 ? `<text x="${bx + barW / 2}" y="${by - 2}" font-size="7" text-anchor="middle" fill="#333">${val}</text>` : ''}`;
        }).join('');
        return `${bars}
          <text x="${gx + (nivCols.length * (barW + 1)) / 2}" y="${chartH + 12}" font-size="8" text-anchor="middle" fill="#333" font-weight="600">${p.label.length > 8 ? p.label.substring(0, 8) + '.' : p.label}</text>`;
      }).join('');

      const legend = nivCols.map((niv, i) => {
        const lx = 10 + i * 44;
        return `<rect x="${lx}" y="${chartH + 20}" width="8" height="8" fill="${NIV_COLORS[i % NIV_COLORS.length]}" rx="1"/>
          <text x="${lx + 11}" y="${chartH + 28}" font-size="8" fill="#333">${niv}</text>`;
      }).join('');

      return `<div style="display:inline-block;vertical-align:top;">
        <div style="font-weight:bold;font-size:11px;margin-bottom:4px;color:#1F4E79;">Effectifs pôle x niveau</div>
        <svg width="${svgW}" height="${svgH}" xmlns="http://www.w3.org/2000/svg">
          <line x1="8" y1="0" x2="8" y2="${chartH}" stroke="#ccc" stroke-width="0.5"/>
          <line x1="8" y1="${chartH}" x2="${svgW}" y2="${chartH}" stroke="#ccc" stroke-width="0.5"/>
          ${groups}${legend}
        </svg>
      </div>`;
    };

    const piePolesHTML = buildPieChart(piePolesData, 140, 'Répartition par pôle');
    const barNiveauxHTML = buildBarChart(barNiveauxData, 200, 'Effectifs par niveau');
    const groupedBarHTML = buildGroupedBarChart();
    const pieFormationHTML = (_csTotal > 0 || _ddTotal > 0)
      ? buildPieChart(pieFormationData, 130, 'Types de formation')
      : '';

    // ── Construction du HTML ──
    const html = `<!DOCTYPE html>
<html lang="fr">
<head>
<meta charset="UTF-8">
<title>Effectifs Détaillés - Institut 2i</title>
<style>
  @page { size: A4 portrait; margin: 12mm 15mm 16mm 15mm;
    @bottom-center { content: "Page " counter(page) " / " counter(pages); font-size: 9px; color: #999; }
  }
  @media print {
    html, body { margin: 0 !important; padding: 0 !important; }
    .page-break { page-break-before: always; }
    thead { display: table-header-group; }
    tfoot { display: table-footer-group; }
    tr { page-break-inside: avoid; }
  }
  body { font-family: 'Agency FB', Arial, Helvetica, sans-serif; font-size: 12px; color: #000; margin: 0; padding: 15px 20px; }
  h2 { font-size: 14px; margin: 18px 0 6px 0; text-transform: uppercase; border-bottom: 2px solid #1F4E79; padding-bottom: 3px; color: #1F4E79; }
  h3 { font-size: 12px; margin: 12px 0 4px 0; color: #333; }
  table { width: 100%; border-collapse: collapse; margin-bottom: 12px; }
  thead th { background: #1F4E79; color: #fff; padding: 5px 8px; font-size: 11px; text-align: center; border: 1px solid #1F4E79; }
  td { padding: 4px 8px; border: 1px solid #999; font-size: 11px; }
  .center { text-align: center; }
  .right { text-align: right; }
  .bold { font-weight: bold; }
  .total-row td { background: #E8EEF4; font-weight: bold; }
  .sub-total td { background: #F5F5F5; font-weight: 600; }
  .cs-color { color: #B45309; }
  .dd-color { color: #7C3AED; }
  .section-break { page-break-before: auto; }
  .no-break { page-break-inside: avoid; }
  .page-break { page-break-before: always; }
  .footer { margin-top: 30px; font-size: 10px; color: #666; display: flex; justify-content: space-between; border-top: 1px solid #ccc; padding-top: 6px; }
  .page-footer { position: fixed; bottom: 0; left: 0; right: 0; text-align: center; font-size: 9px; color: #aaa; padding: 4px 0; }
  .charts-row { display: flex; justify-content: space-around; align-items: flex-start; flex-wrap: wrap; gap: 10px; }
  svg { print-color-adjust: exact; -webkit-print-color-adjust: exact; }
  .signature { margin-top: 30px; text-align: right; font-size: 11px; page-break-inside: avoid; }
</style>
</head>
<body>

<!-- En-tête institutionnel -->
<table style="width:100%;margin-bottom:10px;border:none;">
<tr style="border:none;">
  <td style="width:80px;vertical-align:top;text-align:center;border:none;">
    <img src="/logo.png" alt="Logo" style="width:65px;height:65px;object-fit:contain;" onerror="this.style.display='none'" />
  </td>
  <td style="vertical-align:top;text-align:center;line-height:1.5;border:none;">
    <div style="font-weight:bold;font-size:15px;text-transform:uppercase;">INSTITUT INTERNATIONAL POLYTECHNIQUE & COMMERCE</div>
    <div style="font-size:9px;">Agrément définitif par l'Etat S/N° 196 MES-CAB.DG-DAAC du 21/04/2021</div>
    <div style="font-weight:bold;font-size:12px;margin-top:6px;text-transform:uppercase;">SERVICE DE LA SCOLARITE ET DES EXAMENS</div>
  </td>
</tr>
</table>

<div style="text-align:center;font-weight:bold;font-size:18px;margin:10px 0 4px;text-transform:uppercase;text-decoration:underline;">
  ÉTAT DES EFFECTIFS
</div>
<div style="text-align:center;font-size:12px;margin-bottom:14px;">
  Année Académique : <strong>${anneeAcademique}</strong>
  &nbsp;&nbsp;|&nbsp;&nbsp; Date : <strong>${today}</strong>
  &nbsp;&nbsp;|&nbsp;&nbsp; Effectif total : <strong>${_totalEtudiants}</strong>
</div>

<!-- ═══════════════ GRAPHIQUES ═══════════════ -->
<div style="display:flex;justify-content:space-around;align-items:flex-start;flex-wrap:wrap;margin:8px 0 14px;gap:10px;page-break-inside:avoid;">
  ${piePolesHTML}
  ${barNiveauxHTML}
  ${groupedBarHTML}
  ${pieFormationHTML}
</div>

<!-- ═══════════════ 1. RÉCAPITULATIF GÉNÉRAL ═══════════════ -->
<h2>1. Récapitulatif général par pôle et niveau</h2>
<table>
  <thead>
    <tr>
      <th style="text-align:left;width:25%;">Pôle</th>
      ${nivCols.map(n => `<th>${n}</th>`).join('')}
      <th>Total</th>
      <th>%</th>
    </tr>
  </thead>
  <tbody>
    ${_polesNiveaux.filter(p => Object.values(p.niveaux).some(v => v > 0)).map(p => {
      const total = Object.values(p.niveaux).reduce((s, v) => s + v, 0);
      const pct = _totalEtudiants > 0 ? ((total / _totalEtudiants) * 100).toFixed(1) : '0.0';
      return `<tr class="no-break">
        <td class="bold">${p.label}</td>
        ${nivCols.map(n => `<td class="center">${p.niveaux[n] ?? '—'}</td>`).join('')}
        <td class="center bold">${total}</td>
        <td class="center">${pct}%</td>
      </tr>`;
    }).join('')}
    <tr class="total-row">
      <td>TOTAL</td>
      ${nivCols.map(n => {
        const sum = _polesNiveaux.reduce((s, p) => s + (p.niveaux[n] ?? 0), 0);
        return `<td class="center">${sum}</td>`;
      }).join('')}
      <td class="center">${_totalEtudiants}</td>
      <td class="center">100%</td>
    </tr>
  </tbody>
</table>

<!-- ═══════════════ 2. RÉPARTITION PAR NIVEAU ═══════════════ -->
<h2>2. Répartition par niveau</h2>
<table>
  <thead>
    <tr>
      <th style="text-align:left;width:30%;">Niveau</th>
      <th>Effectif</th>
      <th>% du total</th>
    </tr>
  </thead>
  <tbody>
    ${_statsNiveaux.filter(n => n.count > 0).map(n => {
      const pct = _totalEtudiants > 0 ? ((n.count / _totalEtudiants) * 100).toFixed(1) : '0.0';
      return `<tr class="no-break">
        <td>${n.label} (${n.code})</td>
        <td class="center bold">${n.count}</td>
        <td class="center">${pct}%</td>
      </tr>`;
    }).join('')}
    <tr class="total-row">
      <td>TOTAL</td>
      <td class="center">${_totalEtudiants}</td>
      <td class="center">100%</td>
    </tr>
  </tbody>
</table>

<!-- ═══════════════ 3. EFFECTIFS CS / DD ═══════════════ -->
${(_csTotal > 0 || _ddTotal > 0) ? `
<h2>3. Effectifs Cours du Soir (CS) et Double Diplôme (DD)</h2>

${csDdPoleKeys.length > 0 && csDdNivCols.length > 0 ? `
<table>
  <thead>
    <tr>
      <th style="text-align:left;width:20%;">Pôle</th>
      <th style="width:10%;">Type</th>
      ${csDdNivCols.map(n => `<th>${n}</th>`).join('')}
      <th>Total</th>
    </tr>
  </thead>
  <tbody>
    ${csDdPoleKeys.map(poleCode => {
      const pd = _csDdPN[poleCode];
      const rows: string[] = [];
      if (pd.CS > 0) {
        rows.push(`<tr class="no-break">
          <td class="bold" rowspan="${(pd.CS > 0 ? 1 : 0) + (pd.DD > 0 ? 1 : 0)}">${pd.label}</td>
          <td class="center cs-color bold">CS</td>
          ${csDdNivCols.map(n => `<td class="center">${pd.niveaux[n]?.CS ?? '—'}</td>`).join('')}
          <td class="center bold cs-color">${pd.CS}</td>
        </tr>`);
      }
      if (pd.DD > 0) {
        rows.push(`<tr class="no-break">
          ${pd.CS <= 0 ? `<td class="bold" rowspan="1">${pd.label}</td>` : ''}
          <td class="center dd-color bold">DD</td>
          ${csDdNivCols.map(n => `<td class="center">${pd.niveaux[n]?.DD ?? '—'}</td>`).join('')}
          <td class="center bold dd-color">${pd.DD}</td>
        </tr>`);
      }
      return rows.join('');
    }).join('')}
    <tr class="total-row">
      <td colspan="2">TOTAL CS + DD</td>
      ${csDdNivCols.map(n => {
        const cs = _csDdNiv[n]?.CS ?? 0;
        const dd = _csDdNiv[n]?.DD ?? 0;
        return `<td class="center">${cs + dd > 0 ? cs + dd : '—'}</td>`;
      }).join('')}
      <td class="center">${_csTotal + _ddTotal}</td>
    </tr>
  </tbody>
</table>

<div style="margin-top:4px;font-size:11px;">
  <strong>Synthèse :</strong> CS = <strong class="cs-color">${_csTotal}</strong> étudiant(s)
  &nbsp;|&nbsp; DD = <strong class="dd-color">${_ddTotal}</strong> étudiant(s)
  &nbsp;|&nbsp; FI = <strong>${_totalEtudiants - _csTotal - _ddTotal}</strong> étudiant(s)
</div>
` : ''}
` : ''}

<!-- ═══════════════ 3bis. EFFECTIFS PAR SEXE × NIVEAU × PÔLE (FI / FC) ═══════════════ -->
${(_repSexe.FI.total > 0 || _repSexe.FC.total > 0) ? `
<div class="page-break"></div>
<div style="text-align:center;font-size:10px;color:#666;margin-bottom:8px;">
  ÉTAT DES EFFECTIFS — ${anneeAcademique} (suite)
</div>
<h2>${(_csTotal > 0 || _ddTotal > 0) ? '4' : '3'}. Effectifs par sexe × niveau × pôle (FI et FC)</h2>

${(['FI', 'FC'] as const).map(tf => {
  const data = _repSexe[tf];
  if (!data || data.total === 0) return '';
  const label = tf === 'FI' ? 'Formation Initiale (FI + DD)' : 'Formation Continue (Cours du Soir)';
  const color = tf === 'FI' ? '#1F4E79' : '#B45309';
  const poleKeys = Object.keys(data.poles);

  return `
<h3 style="color:${color};font-size:12px;margin-top:14px;border-left:4px solid ${color};padding-left:8px;">
  ${label} — Total: <strong>${data.total}</strong>
  (H: <strong>${data.total_H}</strong> / F: <strong>${data.total_F}</strong>)
</h3>
<table>
  <thead>
    <tr>
      <th style="text-align:left;width:18%;">Pôle</th>
      <th style="width:8%;">Sexe</th>
      ${nivCols.map(n => `<th>${n}</th>`).join('')}
      <th>Total</th>
    </tr>
  </thead>
  <tbody>
    ${poleKeys.map((pk: string) => {
      const pd: any = data.poles[pk];
      return `
      <tr class="no-break">
        <td class="bold" rowspan="3" style="background:#F5F5F5;">${pd.label}</td>
        <td class="center bold" style="color:#2563EB;">H</td>
        ${nivCols.map(n => `<td class="center">${pd.niveaux?.[n]?.H ?? '—'}</td>`).join('')}
        <td class="center bold" style="color:#2563EB;">${pd.H}</td>
      </tr>
      <tr class="no-break">
        <td class="center bold" style="color:#DB2777;">F</td>
        ${nivCols.map(n => `<td class="center">${pd.niveaux?.[n]?.F ?? '—'}</td>`).join('')}
        <td class="center bold" style="color:#DB2777;">${pd.F}</td>
      </tr>
      <tr class="sub-total no-break">
        <td class="center bold">Total</td>
        ${nivCols.map(n => `<td class="center">${pd.niveaux?.[n]?.total ?? '—'}</td>`).join('')}
        <td class="center bold">${pd.total}</td>
      </tr>
      `;
    }).join('')}
    <tr class="total-row">
      <td colspan="2">TOTAL ${tf}</td>
      ${nivCols.map(n => {
        const sum = poleKeys.reduce((s: number, pk: string) => {
          const pd: any = data.poles[pk];
          return s + (pd.niveaux?.[n]?.total ?? 0);
        }, 0);
        return `<td class="center">${sum > 0 ? sum : '—'}</td>`;
      }).join('')}
      <td class="center">${data.total}</td>
    </tr>
  </tbody>
</table>

<div style="margin-top:4px;font-size:11px;">
  <strong>Synthèse ${tf}:</strong>
  Hommes = <strong style="color:#2563EB;">${data.total_H}</strong> (${data.total > 0 ? ((data.total_H / data.total) * 100).toFixed(1) : '0'}%)
  &nbsp;|&nbsp; Femmes = <strong style="color:#DB2777;">${data.total_F}</strong> (${data.total > 0 ? ((data.total_F / data.total) * 100).toFixed(1) : '0'}%)
</div>
`;
}).join('')}
` : ''}

<!-- ═══════════════ 4. DÉTAIL PAR CLASSE ═══════════════ -->
<div class="page-break"></div>
<div style="text-align:center;font-size:10px;color:#666;margin-bottom:8px;">
  ÉTAT DES EFFECTIFS — ${anneeAcademique} (suite)
</div>
<h2>${(_csTotal > 0 || _ddTotal > 0) ? '5' : '4'}. Détail par classe</h2>
<table>
  <thead>
    <tr>
      <th style="text-align:center;width:35px;">N°</th>
      <th style="text-align:left;width:50%;">Classe</th>
      <th>Effectif</th>
    </tr>
  </thead>
  <tbody>
    ${_classes.filter(c => c.count > 0).map((c, idx) => `<tr class="no-break">
      <td class="center">${idx + 1}</td>
      <td>${c.label}</td>
      <td class="center bold">${c.count}</td>
    </tr>`).join('')}
    <tr class="total-row">
      <td></td>
      <td>TOTAL</td>
      <td class="center">${_classes.reduce((s, c) => s + c.count, 0)}</td>
    </tr>
  </tbody>
</table>

<!-- ═══════════════ 5. DÉTAIL PAR OPTION ═══════════════ -->
<div class="page-break"></div>
<div style="text-align:center;font-size:10px;color:#666;margin-bottom:8px;">
  ÉTAT DES EFFECTIFS — ${anneeAcademique} (suite)
</div>
<h2>${(_csTotal > 0 || _ddTotal > 0) ? '6' : '5'}. Détail par option / filière</h2>
<table>
  <thead>
    <tr>
      <th style="text-align:center;width:35px;">N°</th>
      <th style="text-align:left;width:50%;">Option</th>
      <th>Effectif</th>
      <th>%</th>
    </tr>
  </thead>
  <tbody>
    ${_options.filter(o => o.count > 0).map((o, idx) => {
      const pct = _totalEtudiants > 0 ? ((o.count / _totalEtudiants) * 100).toFixed(1) : '0.0';
      return `<tr class="no-break">
        <td class="center">${idx + 1}</td>
        <td>${o.label}</td>
        <td class="center bold">${o.count}</td>
        <td class="center">${pct}%</td>
      </tr>`;
    }).join('')}
    <tr class="total-row">
      <td></td>
      <td>TOTAL</td>
      <td class="center">${_options.reduce((s, o) => s + o.count, 0)}</td>
      <td class="center">100%</td>
    </tr>
  </tbody>
</table>

<!-- Signature -->
<div class="signature">
  <div>Fait à Pointe-Noire, le ${today}</div>
  <div style="margin-top:12px;font-weight:bold;">Le Chef de Service de la Scolarité & des Examens</div>
  <div style="margin-top:40px;">................................................................</div>
  <div style="margin-top:2px;font-weight:bold;">Donald Christ MBEMBA LOKO</div>
</div>

<div class="footer">
  <span>Institut International 2i </span>
  <span>État des effectifs — ${anneeAcademique}</span>
</div>

</body>
</html>`;

    const w = window.open('', '_blank');
    if (w) {
      w.document.write(html);
      w.document.close();
      w.focus();
      setTimeout(() => w.print(), 600);
    }
  }, [choixFormulaire, anneeAcademique]);

  const allSelected = etudiants.length > 0 && selectedIds.size === etudiants.length;
  const someSelected = selectedIds.size > 0;
  const hasActiveFilters = filters.niveau || filters.option || filters.pole || filters.classe || filters.actif !== 'true' || filters.type_formation;

  // Fix 2026-09-10 : listes classes/options FILTRÉES par niveau+pôle actuels.
  // Le user attend que le dropdown Classe ne montre que les classes du L3
  // sélectionné dans le pôle Commerce, pas toutes les 90+ classes.
  const codeClasseMeta = React.useMemo(() => {
    const map: Record<string, { niveau: string; pole_nom: string; option_nom: string; option_code?: string }> = {};
    (allClasses as any[]).forEach(c => {
      map[c.code] = {
        niveau: c.niveau || '',
        pole_nom: c.pole_nom || '',
        option_nom: c.option_nom || '',
        option_code: c.option_code || '',
      };
    });
    return map;
  }, [allClasses]);

  const classesFiltrees = React.useMemo(() => {
    const base = choixFormulaire?.classes || [];
    return base.filter((c: any) => {
      const meta = codeClasseMeta[c.code];
      if (filters.niveau && meta?.niveau && meta.niveau !== filters.niveau) {
        return false;
      }
      // Fallback si pas de meta : dériver niveau du code (L1MI → L1)
      if (filters.niveau && !meta?.niveau) {
        const prefix = c.code?.match(/^(L1|L2|L3|M1|M2)/i)?.[1]?.toUpperCase();
        if (prefix && prefix !== filters.niveau) return false;
      }
      if (filters.pole && meta?.pole_nom && meta.pole_nom !== filters.pole) {
        return false;
      }
      return true;
    });
  }, [choixFormulaire, codeClasseMeta, filters.niveau, filters.pole]);

  const optionsFiltrees = React.useMemo(() => {
    const base = choixFormulaire?.options || [];
    if (!filters.niveau && !filters.pole) return base;
    // Reconstituer nom_option → code_option depuis allOptions
    const codeParNom: Record<string, string> = {};
    (allOptions as any[]).forEach(o => {
      if (o.nom) codeParNom[o.nom] = o.code;
    });
    // Options présentes dans les classes filtrées (via option_nom)
    const codesRetenus = new Set<string>();
    classesFiltrees.forEach((c: any) => {
      const meta = codeClasseMeta[c.code];
      const code = meta?.option_nom ? codeParNom[meta.option_nom] : '';
      if (code) codesRetenus.add(code);
    });
    // Filtre pôle uniquement (sans niveau) : ajouter toutes les options du pôle
    if (filters.pole) {
      (allOptions as any[]).forEach(o => {
        if (o.pole_nom === filters.pole) codesRetenus.add(o.code);
      });
    }
    if (codesRetenus.size === 0) return base;
    return base.filter((o: any) => codesRetenus.has(o.code));
  }, [choixFormulaire, classesFiltrees, codeClasseMeta, filters.niveau, filters.pole, allOptions]);

  // Reset classe/option si elles ne sont plus dans les listes filtrées
  React.useEffect(() => {
    if (filters.classe && !classesFiltrees.some((c: any) => c.code === filters.classe)) {
      setFilters(f => ({ ...f, classe: '' }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classesFiltrees]);
  React.useEffect(() => {
    if (filters.option && !optionsFiltrees.some((o: any) => o.code === filters.option)) {
      setFilters(f => ({ ...f, option: '' }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [optionsFiltrees]);

  // Stats
  const statsNiveaux = choixFormulaire?.niveaux || [];
  const totalActifs = statsNiveaux.reduce((sum, n) => sum + n.count, 0);
  const statsPoles = choixFormulaire?.poles || [];
  const polesNiveaux = choixFormulaire?.poles_niveaux || [];
  // Niveaux présents dans le breakdown (pour les colonnes)
  const niveauxColonnes = ['L1', 'L2', 'L3', 'M1', 'M2'].filter(niv =>
    polesNiveaux.some(p => (p.niveaux[niv] ?? 0) > 0)
  );

  // CS/DD stats
  const csTotal = choixFormulaire?.cs_total ?? 0;
  const ddTotal = choixFormulaire?.dd_total ?? 0;
  const csDdParNiveau = choixFormulaire?.cs_dd_par_niveau ?? {};
  const csDdNiveaux = ['L1', 'L2', 'L3', 'M1', 'M2'].filter(niv => csDdParNiveau[niv]);
  const csDdParPoleNiveau = choixFormulaire?.cs_dd_par_pole_niveau ?? {};
  const csDdPoles = Object.keys(csDdParPoleNiveau);

  // Pagination : 0 pages quand « Tous » (une seule page virtuelle)
  const totalPages = pageSize > 0 ? Math.ceil(totalCount / pageSize) : 1;

  if (loading && etudiants.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Message (unifié via AlertBanner — refonte vague 3 lot 1) */}
      {message && (
        <AlertBanner
          type={message.type === 'success' ? 'success'
            : message.type === 'error' ? 'error'
              : 'warning'}
          message={message.text}
          onClose={() => setMessage(null)}
        />
      )}

      {/* Header (unifié via PageHeader — refonte vague 3 lot 1) */}
      <PageHeader
        title="Étudiants"
        description={
          <span className="inline-flex items-center gap-1.5">
            <span className="tabular-nums font-medium text-gray-700 dark:text-gray-300">
              {totalCount.toLocaleString('fr-FR')}
            </span>
            <span>inscrits</span>
          </span>
        }
        actions={
          <>
            <Button variant="outline" size="sm" icon={<BarChart3 size={14} />} onClick={handlePrintEffectifs}
              disabled={!choixFormulaire}>
              Effectifs
            </Button>
            <Button variant="outline" size="sm" icon={<Camera size={14} />} onClick={() => setActiveModal('importPhotos')}>
              Photos
            </Button>
            <Button variant="outline" size="sm" icon={<Upload size={14} />} onClick={() => navigate('/etudiants/import')}>
              Import
            </Button>
            <Button variant="outline" size="sm" icon={<RefreshCw size={14} />} onClick={() => navigate('/etudiants/mise-a-jour')}>
              Mise à jour
            </Button>
            <Button variant="outline" size="sm" icon={<Merge size={14} />} onClick={() => setShowFusionClasses(true)} className="text-orange-600 dark:text-orange-400 border-orange-300 dark:border-orange-700">
              Fusion classes
            </Button>
            <Button variant="primary" size="sm" icon={<Plus size={14} />} onClick={() => navigate('/etudiants/new')}>
              Nouvel étudiant
            </Button>
          </>
        }
      />

      {/* Barre stats compacte — TOUJOURS visible, une seule ligne
          Chips cliquables pour filtrer par niveau/pôle. Ne prend qu'une
          seule rangée pour ne pas empiéter sur la liste. */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-2 flex flex-wrap items-center gap-2 text-xs">
        <span className="inline-flex items-center gap-1 font-semibold text-gray-700 dark:text-gray-200">
          <Users size={13} className="text-blue-500" />
          Total <span className="text-blue-600 dark:text-blue-400 font-bold">{totalCount}</span>
        </span>
        <span className="text-gray-300 dark:text-gray-600">·</span>
        {statsNiveaux.filter(n => n.count > 0).map(n => (
          <button
            key={n.code}
            type="button"
            onClick={() => setFilters(f => ({ ...f, niveau: f.niveau === n.code ? '' : n.code }))}
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border transition-colors ${
              filters.niveau === n.code
                ? 'bg-blue-600 text-white border-blue-600'
                : 'border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
            }`}
            title={`Filtrer sur ${n.code}`}
          >
            <span className="font-semibold">{n.code}</span>
            <span className={filters.niveau === n.code ? 'text-white/90' : 'text-gray-500 dark:text-gray-400'}>
              {n.count}
            </span>
          </button>
        ))}
        {statsPoles.filter(p => p.count > 0).length > 0 && (
          <>
            <span className="text-gray-300 dark:text-gray-600 mx-1">|</span>
            {statsPoles.filter(p => p.count > 0).map(p => (
              <button
                key={p.code}
                type="button"
                onClick={() => setFilters(f => ({ ...f, pole: f.pole === p.code ? '' : p.code }))}
                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border transition-colors ${
                  filters.pole === p.code
                    ? 'bg-indigo-600 text-white border-indigo-600'
                    : 'border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
                title={`Filtrer sur ${p.label}`}
              >
                <span className="font-medium truncate max-w-[100px]">{p.label}</span>
                <span className={filters.pole === p.code ? 'text-white/90' : 'text-gray-500 dark:text-gray-400'}>
                  {p.count}
                </span>
              </button>
            ))}
          </>
        )}
        <button
          type="button"
          onClick={() => setShowStats(s => !s)}
          className="ml-auto inline-flex items-center gap-1 px-2 py-0.5 rounded border border-gray-200 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700"
          title={showStats ? 'Masquer le détail par pôle × niveau' : 'Afficher le détail par pôle × niveau'}
        >
          <BarChart3 size={12} />
          {showStats ? 'Masquer stats' : 'Détails stats'}
        </button>
      </div>

      {/* Stats Cards — Par pôle (bloc détaillé, replié par défaut) */}
      {showStats && statsPoles.filter(p => p.count > 0).length > 0 && (
        <div className="space-y-3">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {statsPoles.filter(p => p.count > 0).map(p => (
              <div key={p.code}
                className={`rounded-lg border p-3 cursor-pointer transition-colors ${
                  filters.pole === p.code
                    ? 'border-indigo-400 bg-indigo-50 dark:bg-indigo-900/20'
                    : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:border-indigo-300'
                }`}
                onClick={() => setFilters(f => ({ ...f, pole: f.pole === p.code ? '' : p.code }))}>
                <div className={`text-xl font-bold ${filters.pole === p.code ? 'text-indigo-600 dark:text-indigo-400' : 'text-gray-700 dark:text-gray-300'}`}>
                  {p.count}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate" title={p.label}>
                  {p.label}
                </div>
              </div>
            ))}
          </div>

          {/* Tableau pôle × niveau */}
          {polesNiveaux.length > 0 && niveauxColonnes.length > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
              <div className="px-4 py-2 border-b border-gray-100 dark:border-gray-700">
                <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                  Effectifs par pôle et niveau
                </span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 dark:bg-gray-700/50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Pôle</th>
                      {niveauxColonnes.map(niv => (
                        <th key={niv} className="px-3 py-2 text-center text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">{niv}</th>
                      ))}
                      <th className="px-3 py-2 text-center text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Total</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                    {polesNiveaux.filter(p => Object.values(p.niveaux).some(v => v > 0)).map(p => {
                      const total = Object.values(p.niveaux).reduce((s, v) => s + v, 0);
                      return (
                        <tr key={p.pole} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                          <td className="px-4 py-2 font-medium text-gray-700 dark:text-gray-300 text-xs">{p.label}</td>
                          {niveauxColonnes.map(niv => (
                            <td key={niv} className="px-3 py-2 text-center">
                              {(p.niveaux[niv] ?? 0) > 0 ? (
                                <span
                                  className="inline-block px-2 py-0.5 rounded text-xs font-medium bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 cursor-pointer hover:bg-indigo-100"
                                  onClick={() => setFilters(f => ({ ...f, pole: p.pole, niveau: niv }))}
                                >
                                  {p.niveaux[niv]}
                                </span>
                              ) : (
                                <span className="text-gray-300 dark:text-gray-600">—</span>
                              )}
                            </td>
                          ))}
                          <td className="px-3 py-2 text-center">
                            <span className="text-xs font-bold text-gray-700 dark:text-gray-300">{total}</span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* CS/DD Effectifs */}
      {(csTotal > 0 || ddTotal > 0) && (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="px-4 py-2 border-b border-gray-100 dark:border-gray-700">
            <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
              Effectifs CS / DD
            </span>
          </div>
          <div className="p-4">
            {/* Totaux */}
            <div className="flex gap-4 mb-3">
              {csTotal > 0 && (
                <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 cursor-pointer hover:bg-amber-100 dark:hover:bg-amber-900/30 transition-colors"
                  onClick={() => setFilters(f => ({ ...f, type_formation: f.type_formation === 'CS' ? '' : 'CS' }))}>
                  <Moon size={16} className="text-amber-600 dark:text-amber-400" />
                  <span className="text-sm font-medium text-amber-800 dark:text-amber-300">Cours du Soir (CS)</span>
                  <span className="text-lg font-bold text-amber-700 dark:text-amber-300 ml-1">{csTotal}</span>
                </div>
              )}
              {ddTotal > 0 && (
                <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 cursor-pointer hover:bg-purple-100 dark:hover:bg-purple-900/30 transition-colors"
                  onClick={() => setFilters(f => ({ ...f, type_formation: f.type_formation === 'DD' ? '' : 'DD' }))}>
                  <BookOpen size={16} className="text-purple-600 dark:text-purple-400" />
                  <span className="text-sm font-medium text-purple-800 dark:text-purple-300">Double Diplôme (DD)</span>
                  <span className="text-lg font-bold text-purple-700 dark:text-purple-300 ml-1">{ddTotal}</span>
                </div>
              )}
            </div>

            {/* Tableau CS/DD par pôle et niveau */}
            {csDdPoles.length > 0 && csDdNiveaux.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 dark:bg-gray-700/50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Pôle</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Type</th>
                      {csDdNiveaux.map(niv => (
                        <th key={niv} className="px-3 py-2 text-center text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">{niv}</th>
                      ))}
                      <th className="px-3 py-2 text-center text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Total</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                    {csDdPoles.map(poleCode => {
                      const poleData = csDdParPoleNiveau[poleCode];
                      const hasCS = poleData.CS > 0;
                      const hasDD = poleData.DD > 0;
                      const rowCount = (hasCS ? 1 : 0) + (hasDD ? 1 : 0);
                      return (
                        <React.Fragment key={poleCode}>
                          {hasCS && (
                            <tr className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                              <td className="px-4 py-2 font-medium text-gray-700 dark:text-gray-300 text-xs" rowSpan={rowCount}>
                                {poleData.label}
                              </td>
                              <td className="px-3 py-2 text-xs font-medium text-amber-700 dark:text-amber-400">CS</td>
                              {csDdNiveaux.map(niv => (
                                <td key={niv} className="px-3 py-2 text-center">
                                  {(poleData.niveaux[niv]?.CS ?? 0) > 0 ? (
                                    <span className="inline-block px-2 py-0.5 rounded text-xs font-medium bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 cursor-pointer hover:bg-amber-100"
                                      onClick={() => setFilters(f => ({ ...f, pole: poleCode, type_formation: 'CS', niveau: niv }))}>
                                      {poleData.niveaux[niv].CS}
                                    </span>
                                  ) : <span className="text-gray-300 dark:text-gray-600">—</span>}
                                </td>
                              ))}
                              <td className="px-3 py-2 text-center text-xs font-bold text-amber-700 dark:text-amber-300">{poleData.CS}</td>
                            </tr>
                          )}
                          {hasDD && (
                            <tr className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                              {!hasCS && (
                                <td className="px-4 py-2 font-medium text-gray-700 dark:text-gray-300 text-xs" rowSpan={rowCount}>
                                  {poleData.label}
                                </td>
                              )}
                              <td className="px-3 py-2 text-xs font-medium text-purple-700 dark:text-purple-400">DD</td>
                              {csDdNiveaux.map(niv => (
                                <td key={niv} className="px-3 py-2 text-center">
                                  {(poleData.niveaux[niv]?.DD ?? 0) > 0 ? (
                                    <span className="inline-block px-2 py-0.5 rounded text-xs font-medium bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 cursor-pointer hover:bg-purple-100"
                                      onClick={() => setFilters(f => ({ ...f, pole: poleCode, type_formation: 'DD', niveau: niv }))}>
                                      {poleData.niveaux[niv].DD}
                                    </span>
                                  ) : <span className="text-gray-300 dark:text-gray-600">—</span>}
                                </td>
                              ))}
                              <td className="px-3 py-2 text-center text-xs font-bold text-purple-700 dark:text-purple-300">{poleData.DD}</td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                    {/* Ligne totaux */}
                    <tr className="bg-gray-50 dark:bg-gray-700/50 font-semibold">
                      <td className="px-4 py-2 text-xs text-gray-700 dark:text-gray-300" colSpan={2}>Total</td>
                      {csDdNiveaux.map(niv => {
                        const csNiv = csDdParNiveau[niv]?.CS ?? 0;
                        const ddNiv = csDdParNiveau[niv]?.DD ?? 0;
                        const total = csNiv + ddNiv;
                        return (
                          <td key={niv} className="px-3 py-2 text-center text-xs text-gray-700 dark:text-gray-300">
                            {total > 0 ? total : '—'}
                          </td>
                        );
                      })}
                      <td className="px-3 py-2 text-center text-xs font-bold text-gray-700 dark:text-gray-300">{csTotal + ddTotal}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Alerte doublons */}
      {doublonsData && doublonsData.total_doublons > 0 && (
        <div className="border border-amber-300 dark:border-amber-700 rounded-lg bg-amber-50 dark:bg-amber-900/20 overflow-hidden">
          <button
            onClick={() => setShowDoublons(!showDoublons)}
            className="w-full flex items-center justify-between px-4 py-3 hover:bg-amber-100/50 dark:hover:bg-amber-900/30 transition-colors"
          >
            <div className="flex items-center gap-2">
              <AlertTriangle size={18} className="text-amber-600 dark:text-amber-400" />
              <span className="font-semibold text-amber-800 dark:text-amber-300 text-sm">
                {doublonsData.total_doublons} doublon(s) potentiel(s) détecté(s)
              </span>
              <span className="text-xs text-amber-600 dark:text-amber-500">
                ({doublonsData.total_groupes} groupe{doublonsData.total_groupes > 1 ? 's' : ''} sur {doublonsData.total_etudiants_analyses} étudiants analysés)
              </span>
            </div>
            <ChevronRight size={16} className={`text-amber-600 dark:text-amber-400 transition-transform ${showDoublons ? 'rotate-90' : ''}`} />
          </button>

          {showDoublons && (
            <div className="px-4 pb-4 space-y-3">
              <p className="text-xs text-amber-700 dark:text-amber-400">
                L'algorithme compare les noms, prénoms, dates de naissance et matricules pour détecter les doublons potentiels.
                Vérifiez chaque groupe avant de fusionner.
              </p>

              {doublonsData.groupes.map((groupe, gi) => (
                <div key={gi} className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                  {/* Ligne principal */}
                  <div className="px-4 py-2.5 bg-gray-50 dark:bg-gray-700/50 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="text-xs font-bold text-gray-400">#{gi + 1}</span>
                      <div>
                        <span className="font-semibold text-sm text-gray-900 dark:text-gray-100">
                          {groupe.principal.nom} {groupe.principal.prenoms}
                        </span>
                        <span className="ml-2 text-xs text-gray-500 font-mono">{groupe.principal.matricule}</span>
                        {groupe.principal.classe && (
                          <Badge variant="info" className="ml-2 text-[10px]">{groupe.principal.classe}</Badge>
                        )}
                        {groupe.principal.date_naissance && (
                          <span className="ml-2 text-[10px] text-gray-400">né(e) le {groupe.principal.date_naissance}</span>
                        )}
                      </div>
                    </div>
                    <Badge variant="warning" className="text-xs">
                      {groupe.doublons.length} doublon{groupe.doublons.length > 1 ? 's' : ''}
                    </Badge>
                  </div>

                  {/* Doublons */}
                  <div className="divide-y divide-gray-100 dark:divide-gray-700">
                    {groupe.doublons.map((d) => {
                      const pct = Math.round(d.score * 100);
                      const pctNom = Math.round(d.score_nom * 100);
                      const couleurScore = pct >= 95 ? 'text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/30' :
                        pct >= 88 ? 'text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/30' :
                        'text-yellow-600 dark:text-yellow-400 bg-yellow-50 dark:bg-yellow-900/30';

                      return (
                        <div key={d.id} className="px-4 py-2.5 flex items-center justify-between gap-3">
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            <div className={`px-2 py-0.5 rounded text-xs font-bold ${couleurScore}`}>
                              {pct}%
                            </div>
                            <div className="min-w-0">
                              <span className="text-sm text-gray-800 dark:text-gray-200">
                                {d.nom} {d.prenoms}
                              </span>
                              <span className="ml-2 text-xs text-gray-500 font-mono">{d.matricule}</span>
                              {d.classe && (
                                <Badge variant="default" className="ml-2 text-[10px]">{d.classe}</Badge>
                              )}
                              {d.date_naissance && (
                                <span className="ml-2 text-[10px] text-gray-400">né(e) le {d.date_naissance}</span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            {/* Tags raisons */}
                            <div className="flex gap-1 flex-wrap">
                              {d.raisons.includes('nom_quasi_identique') && (
                                <span className="px-1.5 py-0.5 rounded text-[9px] bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300">
                                  Nom ≈ identique ({pctNom}%)
                                </span>
                              )}
                              {d.raisons.includes('nom_similaire') && !d.raisons.includes('nom_quasi_identique') && (
                                <span className="px-1.5 py-0.5 rounded text-[9px] bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300">
                                  Nom similaire ({pctNom}%)
                                </span>
                              )}
                              {d.raisons.includes('meme_date_naissance') && (
                                <span className="px-1.5 py-0.5 rounded text-[9px] bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300">
                                  Même date naiss.
                                </span>
                              )}
                              {d.raisons.includes('matricule_similaire') && (
                                <span className="px-1.5 py-0.5 rounded text-[9px] bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300">
                                  Matricule ≈
                                </span>
                              )}
                            </div>
                            {/* Bouton fusionner */}
                            <Button
                              variant="danger"
                              size="sm"
                              disabled={fusionning}
                              onClick={async () => {
                                if (!window.confirm(
                                  `Fusionner ${d.matricule} (${d.nom} ${d.prenoms}) vers ${groupe.principal.matricule} (${groupe.principal.nom} ${groupe.principal.prenoms}) ?\n\nLes notes du doublon seront transférées et le doublon sera désactivé.`
                                )) return;
                                try {
                                  const result = await fusionnerEtudiant({
                                    principalId: groupe.principal.id,
                                    doublonId: d.id,
                                  }).unwrap();
                                  setMessage({
                                    type: 'success',
                                    text: `${result.message} — ${result.transferts.notes_devoirs} note(s) devoirs + ${result.transferts.notes_examens} note(s) examens transférées`,
                                  });
                                  refetchDoublons();
                                  loadEtudiants();
                                } catch (err: any) {
                                  setMessage({ type: 'error', text: err?.data?.error || 'Erreur lors de la fusion' });
                                }
                              }}
                            >
                              Fusionner
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Search Bar + Filters Toggle */}
      <Card>
        <form onSubmit={handleSearch}>
          <div className="flex gap-3 items-center">
            <div className="relative flex-1">
              <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Rechercher par matricule, nom, prénom..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              />
            </div>
            <Button type="submit" variant="primary" size="sm" icon={<Search size={14} />}>Rechercher</Button>
            <button
              type="button"
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${
                hasActiveFilters ? 'border-blue-400 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300' : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              <Filter size={14} />
              Filtres
              {hasActiveFilters && <span className="w-2 h-2 bg-blue-600 rounded-full"></span>}
            </button>
          </div>

          {/* Expandable Filters */}
          {showFilters && (
            <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700">
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                <select value={filters.niveau}
                  onChange={(e) => setFilters(f => ({ ...f, niveau: e.target.value }))}
                  className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500">
                  <option value="">Tous les niveaux</option>
                  <option value="L1">L1</option><option value="L2">L2</option><option value="L3">L3</option>
                  <option value="M1">M1</option><option value="M2">M2</option>
                </select>

                <select value={filters.pole}
                  onChange={(e) => setFilters(f => ({ ...f, pole: e.target.value }))}
                  className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500">
                  <option value="">Tous les pôles</option>
                  {choixFormulaire?.poles_choix?.map(p => (
                    <option key={p.code} value={p.code}>{p.label}</option>
                  ))}
                </select>

                <select value={filters.classe}
                  onChange={(e) => setFilters(f => ({ ...f, classe: e.target.value }))}
                  className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500">
                  <option value="">Toutes les classes ({classesFiltrees.length})</option>
                  {classesFiltrees.map((c: any) => (
                    <option key={c.code} value={c.code}>{c.label} ({c.count})</option>
                  ))}
                </select>

                <select value={filters.option}
                  onChange={(e) => setFilters(f => ({ ...f, option: e.target.value }))}
                  className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500">
                  <option value="">Toutes les options ({optionsFiltrees.length})</option>
                  {optionsFiltrees.map((o: any) => (
                    <option key={o.code} value={o.code}>{o.label} ({o.count})</option>
                  ))}
                </select>

                <select value={filters.actif}
                  onChange={(e) => setFilters(f => ({ ...f, actif: e.target.value }))}
                  className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500">
                  <option value="">Tous</option>
                  <option value="true">Actifs</option>
                  <option value="false">Inactifs</option>
                </select>

                <select value={filters.type_formation}
                  onChange={(e) => setFilters(f => ({ ...f, type_formation: e.target.value }))}
                  className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500">
                  <option value="">Toutes formations</option>
                  <option value="FI">Formation Initiale</option>
                  <option value="CS">Cours du Soir</option>
                  <option value="DD">Double Diplome</option>
                </select>
              </div>

              <div className="flex items-center gap-2 mt-3">
                <Button type="button" variant="outline" size="sm" onClick={resetFilters}>
                  Réinitialiser les filtres
                </Button>
                <div className="ml-auto flex gap-2">
                  <Button type="button" variant="outline" size="sm" icon={<Download size={14} />} onClick={handleExport}>
                    Exporter Excel
                  </Button>
                  <Button type="button" variant="outline" size="sm" icon={<Printer size={14} />} onClick={() => setActiveModal('printConfig')}>
                    Imprimer
                  </Button>
                </div>
              </div>
            </div>
          )}
        </form>
      </Card>

      {/* Bulk Actions Bar */}
      {someSelected && (
        <div className="flex items-center gap-3 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg animate-in">
          <CheckSquare size={18} className="text-blue-600" />
          <span className="text-sm font-medium text-blue-800 dark:text-blue-300">
            {selectedIds.size} sélectionné(s)
          </span>
          <div className="flex gap-2 ml-auto">
            <Button variant="danger" size="sm" icon={<Trash2 size={14} />}
              onClick={() => setActiveModal('deleteSelected')} disabled={deletingMultiple}>
              Supprimer
            </Button>
            <Button variant="outline" size="sm" onClick={() => setSelectedIds(new Set())}>
              Désélectionner
            </Button>
          </div>
        </div>
      )}

      {/* Students Table — refonte 2026-08-28 :
          - Lignes verticales entre colonnes (border-r sur th/td, sauf dernière)
          - Nom + prénoms sur 2 lignes distinctes (comme SaisieMatricielle)
          - Alignement gauche renforcé (text-left explicite)
          - Padding cohérent avec le dashboard */}
      <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700 [&_th]:border-r [&_th]:border-gray-200 dark:[&_th]:border-gray-700 [&_th:last-child]:border-r-0">
              <tr>
                <th className="px-3 py-2.5 text-center w-10">
                  <button onClick={handleSelectAll} className="text-gray-400 hover:text-blue-600">
                    {allSelected ? <CheckSquare size={14} className="text-blue-600" /> : <Square size={14} />}
                  </button>
                </th>
                <th className="px-2 py-2.5 text-left text-[11px] font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide">N°</th>
                <th className="px-3 py-2.5 text-left text-[11px] font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide">Matricule</th>
                <th className="px-3 py-2.5 text-left text-[11px] font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide">Nom &amp; Prénoms</th>
                <th className="px-3 py-2.5 text-left text-[11px] font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide">Niveau</th>
                <th className="px-3 py-2.5 text-left text-[11px] font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide">Classe</th>
                <th className="px-3 py-2.5 text-left text-[11px] font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide">Option</th>
                <th className="px-3 py-2.5 text-left text-[11px] font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide">Pôle</th>
                <th className="px-3 py-2.5 text-center text-[11px] font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide">Formation</th>
                <th className="px-3 py-2.5 text-center text-[11px] font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide">Statut</th>
                <th className="px-3 py-2.5 text-center text-[11px] font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800 [&_tr>td]:border-r [&_tr>td]:border-gray-100 dark:[&_tr>td]:border-gray-800 [&_tr>td:last-child]:border-r-0">
              {etudiants.length === 0 ? (
                <tr>
                  <td colSpan={11} className="px-6 py-16 text-center border-r-0">
                    <GraduationCap size={44} className="mx-auto text-gray-300 dark:text-gray-700 mb-3" />
                    <p className="text-gray-500 dark:text-gray-400 font-medium">Aucun étudiant trouvé</p>
                    <p className="text-gray-400 text-xs mt-1">Importez des étudiants via Excel ou ajoutez-en manuellement</p>
                  </td>
                </tr>
              ) : (
                etudiants.map((etudiant, index) => (
                  <tr key={etudiant.id}
                    className={`transition-colors ${selectedIds.has(etudiant.id) ? 'bg-blue-50 dark:bg-blue-900/20' : 'hover:bg-gray-50 dark:hover:bg-gray-800/40'}`}>
                    <td className="px-3 py-2 text-center">
                      <button onClick={() => handleSelectOne(etudiant.id)} className="text-gray-400 hover:text-blue-600">
                        {selectedIds.has(etudiant.id) ? <CheckSquare size={14} className="text-blue-600" /> : <Square size={14} />}
                      </button>
                    </td>
                    <td className="px-2 py-2 text-left text-xs text-gray-400 tabular-nums">{(currentPage - 1) * pageSize + index + 1}</td>
                    <td className="px-3 py-2 text-left text-xs font-mono font-medium text-gray-800 dark:text-gray-200 whitespace-nowrap">{etudiant.matricule}</td>
                    <td className="px-3 py-2 text-left text-sm text-gray-900 dark:text-gray-100">
                      <div className="font-semibold uppercase tracking-wide truncate" title={`${etudiant.nom} ${etudiant.prenoms || etudiant.prenom || ''}`}>
                        {etudiant.nom}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                        {etudiant.prenoms || etudiant.prenom || '—'}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-left">
                      <Badge variant="info" size="sm">{etudiant.niveau}</Badge>
                    </td>
                    <td className="px-3 py-2 text-left text-xs text-gray-700 dark:text-gray-300 whitespace-nowrap">{etudiant.classe || '—'}</td>
                    <td className="px-3 py-2 text-left text-xs text-gray-700 dark:text-gray-300 truncate">{etudiant.option || '—'}</td>
                    <td className="px-3 py-2 text-left text-xs text-gray-700 dark:text-gray-300 truncate">{etudiant.pole || '—'}</td>
                    <td className="px-3 py-2 text-center">
                      {etudiant.type_formation === 'CS' ? (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300">CS</span>
                      ) : etudiant.type_formation === 'DD' ? (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300">DD</span>
                      ) : (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300">FI</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-center">
                      <Badge variant={etudiant.actif ? 'success' : 'danger'} size="sm">
                        {etudiant.actif ? 'Actif' : 'Inactif'}
                      </Badge>
                    </td>
                    <td className="px-3 py-2 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <button onClick={() => navigate(`/etudiants/${etudiant.id}`)}
                          className="p-1 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded" title="Voir">
                          <Eye size={14} />
                        </button>
                        <button onClick={() => navigate(`/etudiants/${etudiant.id}/edit`)}
                          className="p-1 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded" title="Modifier">
                          <Edit size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Table Footer with Pagination */}
        {etudiants.length > 0 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 dark:border-gray-700 text-sm text-gray-500 dark:text-gray-400 gap-3 flex-wrap">
            <div className="flex items-center gap-3">
              <span>
                {pageSize === 0
                  ? `${totalCount} étudiant(s) affichés (tous)`
                  : totalCount > pageSize
                    ? `${(currentPage - 1) * pageSize + 1}-${Math.min(currentPage * pageSize, totalCount)} sur ${totalCount} étudiant(s)`
                    : `${totalCount} étudiant(s) affichés`}
              </span>
              <div className="flex items-center gap-1.5">
                <label htmlFor="etu-pagesize" className="text-xs">Afficher</label>
                <select
                  id="etu-pagesize"
                  value={pageSize}
                  onChange={(e) => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}
                  className="text-xs bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded px-1.5 py-1 text-gray-700 dark:text-gray-200"
                >
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                  <option value={150}>150</option>
                  <option value={0}>Tous</option>
                </select>
                <span className="text-xs">par page</span>
              </div>
            </div>
            {pageSize > 0 && totalPages > 1 && (
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                  className="px-2 py-1 text-xs rounded border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  1
                </button>
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <ChevronLeft size={16} />
                </button>
                {/* Page numbers */}
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let page: number;
                  if (totalPages <= 5) {
                    page = i + 1;
                  } else if (currentPage <= 3) {
                    page = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    page = totalPages - 4 + i;
                  } else {
                    page = currentPage - 2 + i;
                  }
                  return (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`px-2.5 py-1 text-xs rounded border transition-colors ${
                        currentPage === page
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
                      }`}
                    >
                      {page}
                    </button>
                  );
                })}
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <ChevronRight size={16} />
                </button>
                <button
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages}
                  className="px-2 py-1 text-xs rounded border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {totalPages}
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ============== MODALS ============== */}

      {/* Delete Selected Modal */}
      {activeModal === 'deleteSelected' && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-red-100 rounded-full"><AlertTriangle size={24} className="text-red-600" /></div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Confirmer la suppression</h3>
            </div>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Vous allez supprimer <strong>{selectedIds.size} étudiant(s)</strong>. Cette action est irréversible.
            </p>
            <div className="flex gap-3 justify-end">
              <Button variant="outline" size="sm" onClick={() => setActiveModal('none')}>Annuler</Button>
              <Button variant="danger" size="sm" onClick={handleDeleteSelected} disabled={deletingMultiple}
                icon={deletingMultiple ? <Spinner size="sm" /> : <Trash2 size={14} />}>
                Supprimer
              </Button>
            </div>
          </div>
        </div>
      )}


      {/* Import Photos Modal */}
      {activeModal === 'importPhotos' && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Image size={20} className="text-blue-600" />
                Import de Photos en Masse
              </h3>
              <button onClick={() => { setActiveModal('none'); setPhotoFiles([]); setPhotoResult(null); }}
                className="p-1 hover:bg-gray-100 rounded"><X size={20} /></button>
            </div>

            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-4">
              <h4 className="font-medium text-blue-800 dark:text-blue-300 mb-2">Convention de nommage</h4>
              <ul className="text-sm text-blue-700 dark:text-blue-400 space-y-1">
                <li><strong>Par matricule (recommandé) :</strong> MATRICULE.jpg (ex: 2024L1001.jpg)</li>
                <li><strong>Par nom :</strong> NOM_PRENOM.jpg (ex: DUPONT_JEAN.jpg)</li>
              </ul>
              <p className="text-xs text-blue-600 mt-2">Formats acceptés: JPG, PNG, WEBP</p>
            </div>

            <div className="mb-4">
              <input ref={photoInputRef} type="file" multiple accept="image/*"
                onChange={handlePhotoFilesSelect} className="hidden" />
              <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6 text-center cursor-pointer hover:border-blue-400 transition-colors"
                onClick={() => photoInputRef.current?.click()}>
                {photoFiles.length > 0 ? (
                  <div>
                    <Camera size={32} className="mx-auto text-blue-500 mb-2" />
                    <p className="text-sm font-medium text-gray-700">{photoFiles.length} photo(s) sélectionnée(s)</p>
                    <p className="text-xs text-gray-500 mt-1">
                      {(photoFiles.reduce((s, f) => s + f.size, 0) / 1024 / 1024).toFixed(1)} MB
                    </p>
                  </div>
                ) : (
                  <div>
                    <Image size={32} className="mx-auto text-gray-400 mb-2" />
                    <p className="text-sm text-gray-600">Cliquez pour sélectionner des photos</p>
                  </div>
                )}
              </div>
            </div>

            {photoResult && (
              <div className="mb-4 space-y-3">
                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-blue-50 p-3 rounded-lg text-center">
                    <div className="text-xl font-bold text-blue-600">{photoResult.total}</div>
                    <div className="text-xs text-blue-600">Total</div>
                  </div>
                  <div className="bg-green-50 p-3 rounded-lg text-center">
                    <div className="text-xl font-bold text-green-600">{photoResult.associees}</div>
                    <div className="text-xs text-green-600">Associées</div>
                  </div>
                  <div className="bg-red-50 p-3 rounded-lg text-center">
                    <div className="text-xl font-bold text-red-600">{photoResult.non_associees}</div>
                    <div className="text-xs text-red-600">Non trouvées</div>
                  </div>
                </div>
                {photoResult.details.length > 0 && (
                  <div className="max-h-40 overflow-y-auto border rounded-lg">
                    <table className="w-full text-xs">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr><th className="p-2 text-left">Fichier</th><th className="p-2 text-left">Statut</th><th className="p-2 text-left">Détails</th></tr>
                      </thead>
                      <tbody>
                        {photoResult.details.map((d: any, i: number) => (
                          <tr key={i} className="border-t">
                            <td className="p-2">{d.fichier}</td>
                            <td className="p-2">
                              {d.status === 'ok' ?
                                <span className="text-green-600 font-medium">OK</span> :
                                <span className="text-red-600 font-medium">Non trouvé</span>}
                            </td>
                            <td className="p-2 text-gray-500">{d.nom || d.message || ''}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            <div className="flex gap-3 justify-end">
              <Button variant="outline" size="sm"
                onClick={() => { setActiveModal('none'); setPhotoFiles([]); setPhotoResult(null); }}>
                Fermer
              </Button>
              <Button variant="primary" size="sm"
                icon={importingPhotos ? <Spinner size="sm" /> : <Upload size={14} />}
                onClick={handleImportPhotos}
                disabled={photoFiles.length === 0 || importingPhotos}>
                Importer {photoFiles.length > 0 ? `(${photoFiles.length})` : ''}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Print Configuration Modal */}
      {activeModal === 'printConfig' && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-lg w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Printer size={20} className="text-blue-600" />
                Configuration de l'impression
              </h3>
              <button onClick={() => setActiveModal('none')}
                className="p-1 hover:bg-gray-100 rounded"><X size={20} /></button>
            </div>

            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              Sélectionnez les colonnes à inclure dans l'impression ({etudiants.length} étudiant(s) seront imprimés).
            </p>

            <div className="grid grid-cols-2 gap-2 mb-4">
              {printColumns.map((col, idx) => (
                <label key={col.key}
                  className={`flex items-center gap-2.5 p-2.5 rounded-lg border cursor-pointer transition-colors ${
                    col.enabled ? 'border-blue-300 dark:border-blue-600 bg-blue-50 dark:bg-blue-900/30' : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}>
                  <input type="checkbox" checked={col.enabled}
                    onChange={() => {
                      setPrintColumns(prev => prev.map((c, i) =>
                        i === idx ? { ...c, enabled: !c.enabled } : c
                      ));
                    }}
                    className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500" />
                  <span className={`text-sm ${col.enabled ? 'font-medium text-blue-800 dark:text-blue-300' : 'text-gray-600 dark:text-gray-400'}`}>
                    {col.label}
                  </span>
                </label>
              ))}
            </div>

            {/* Colonne Signature (émargement) */}
            <div className="border-t border-gray-100 dark:border-gray-700 pt-3 mb-4">
              <label className={`flex items-center gap-2.5 p-2.5 rounded-lg border cursor-pointer transition-colors ${
                printSignatureCol ? 'border-blue-300 dark:border-blue-600 bg-blue-50 dark:bg-blue-900/30' : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}>
                <input type="checkbox" checked={printSignatureCol}
                  onChange={() => setPrintSignatureCol(!printSignatureCol)}
                  className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500" />
                <div>
                  <span className={`text-sm ${printSignatureCol ? 'font-medium text-blue-800 dark:text-blue-300' : 'text-gray-600 dark:text-gray-400'}`}>
                    Colonne Signature / Émargement
                  </span>
                  <p className="text-xs text-gray-400">Colonne vide pour signature manuelle</p>
                </div>
              </label>
            </div>

            <div className="flex items-center gap-2 mb-4">
              <button type="button"
                className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                onClick={() => setPrintColumns(prev => prev.map(c => ({ ...c, enabled: true })))}>
                Tout sélectionner
              </button>
              <span className="text-gray-300">|</span>
              <button type="button"
                className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                onClick={() => setPrintColumns(DEFAULT_PRINT_COLUMNS.map(c => ({ ...c })))}>
                Par défaut
              </button>
            </div>

            <div className="flex gap-3 justify-end">
              <Button variant="outline" size="sm" onClick={() => setActiveModal('none')}>
                Annuler
              </Button>
              <Button variant="primary" size="sm" icon={<Printer size={14} />}
                onClick={handlePrint}
                disabled={printColumns.filter(c => c.enabled).length === 0}>
                Imprimer
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Fusion des Classes */}
      {showFusionClasses && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="px-6 py-4 border-b border-orange-200 dark:border-orange-800 bg-orange-50 dark:bg-orange-900/10 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
                  <Merge size={20} className="text-orange-500" />
                  Fusion des classes en double
                </h3>
                {classesDoublons && (
                  <p className="text-xs text-orange-700 dark:text-orange-300 mt-0.5">
                    {classesDoublons.total_groupes} groupe(s) détecté(s) — {classesDoublons.total_etudiants_a_fusionner} étudiant(s) à fusionner
                  </p>
                )}
              </div>
              <button onClick={() => setShowFusionClasses(false)} className="text-gray-400 hover:text-gray-600">
                <X size={22} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              {!classesDoublons ? (
                <div className="flex justify-center py-12"><RefreshCw size={24} className="animate-spin text-gray-400" /></div>
              ) : classesDoublons.doublons.length === 0 ? (
                <div className="text-center py-16">
                  <CheckCircle size={48} className="mx-auto text-green-400 mb-3" />
                  <p className="text-lg font-medium text-gray-600 dark:text-gray-300">Aucun doublon de classe détecté</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {classesDoublons.doublons.map((groupe) => (
                    <div key={groupe.forme_normalisee} className="border-2 border-orange-200 dark:border-orange-800 rounded-xl overflow-hidden">
                      <div className="bg-orange-50 dark:bg-orange-900/10 px-4 py-3 flex items-center justify-between">
                        <div>
                          <span className="font-bold text-sm text-gray-900 dark:text-gray-100">
                            {groupe.classe_principale}
                          </span>
                          <span className="text-xs text-gray-500 ml-2">
                            ({groupe.effectif_principal} étudiants — classe principale)
                          </span>
                        </div>
                        <span className="px-2 py-0.5 bg-orange-600 text-white text-xs font-bold rounded-full">
                          +{groupe.total_a_fusionner} à fusionner
                        </span>
                      </div>
                      <div className="divide-y divide-gray-100 dark:divide-gray-700">
                        {groupe.variantes.map((v) => (
                          <div key={v.classe} className="px-4 py-3 flex items-center justify-between gap-3">
                            <div className="flex items-center gap-3">
                              <span className="font-mono text-sm font-bold text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 px-2 py-1 rounded">{v.classe}</span>
                              <ArrowRightIcon size={16} className="text-gray-400" />
                              <span className="font-mono text-sm font-bold text-green-700 dark:text-green-300 bg-green-50 dark:bg-green-900/20 px-2 py-1 rounded">{groupe.classe_principale}</span>
                              <span className="text-xs text-gray-500">({v.effectif} étudiant{v.effectif > 1 ? 's' : ''})</span>
                            </div>
                            <button
                              disabled={fusionnantClasses}
                              onClick={async () => {
                                if (!window.confirm(
                                  `Fusionner ${v.effectif} étudiant(s) de ${v.classe} vers ${groupe.classe_principale} ?\n\n` +
                                  `Les étudiants de ${v.classe} auront :\n` +
                                  `- Classe : ${groupe.classe_principale}\n` +
                                  `- Option et pôle alignés sur ${groupe.classe_principale}\n\n` +
                                  `Cette action est irréversible.`
                                )) return;
                                try {
                                  const res = await fusionnerClasses({ source: v.classe, destination: groupe.classe_principale }).unwrap();
                                  setMessage({ type: 'success', text: res.message });
                                  refetchClassesDoublons();
                                  loadEtudiants();
                                } catch (err: any) {
                                  setMessage({ type: 'error', text: err?.data?.error || 'Erreur de fusion' });
                                }
                              }}
                              className="flex items-center gap-1.5 px-3 py-1.5 bg-orange-600 text-white text-xs font-medium rounded-lg hover:bg-orange-700 disabled:opacity-50 shrink-0"
                            >
                              <Merge size={12} /> Fusionner
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="px-6 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/30 flex items-center justify-between">
              <p className="text-xs text-gray-500">
                La classe principale est celle avec le plus d'étudiants. Les étudiants fusionnés héritent de l'option et du pôle de la classe principale.
              </p>
              <button onClick={() => setShowFusionClasses(false)}
                className="px-4 py-2 text-sm bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-300">
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bouton flottant « Retour en haut » */}
      <ScrollToTop threshold={500} />
    </div>
  );
};
